const { VITE_ENVIRONMENT } = import.meta.env
// 开发环境 cdn 图片地址前缀
const CDN_TEST_IMAGE_PREFIX = 'https://cdn.data-test.byecode.com/userdata'
// 生产环境 cdn 图片地址前缀
const CDN__PROD_IMAGE_PREFIX = 'https://cdn.data.byecode.com/userdata'

// export const CDN_IMAGE_PREFIX = VITE_ENVIRONMENT
//     ? VITE_ENVIRONMENT === 'prod' || VITE_ENVIRONMENT === 'pre'
//         ? CDN__PROD_IMAGE_PREFIX
//         : CDN_TEST_IMAGE_PREFIX
//     : CDN_TEST_IMAGE_PREFIX

export const CDN_IMAGE_PREFIX = CDN__PROD_IMAGE_PREFIX
// export const CDN_IMAGE_PREFIX = CDN_TEST_IMAGE_PREFIX
export const getImageFullUrlInCommon = <T extends string | undefined>(url: T) => {
    if (!url) {
        return url
    }
    if (url.startsWith('http')) {
        return url
    }
    return `${CDN_IMAGE_PREFIX}/${url}` as T
}

export const getImageFullUrlInApplication = <T extends string | undefined>(
    appId: string | undefined,
    url: T,
    options?: { width?: number; height?: number; quality?: number; compress?: boolean }
) => {
    if (!url || !appId) {
        return url
    }
    if (url.startsWith('http')) {
        return url
    }

    const imageUrl = `${CDN_IMAGE_PREFIX}/${url}`
    let suffix = imageUrl.includes('?') ? `&appId=${appId}` : `?appId=${appId}`

    // 阿里云OSS图片处理：根据压缩开关决定是否处理图片
    if ((options?.compress ?? true) && ((options?.width && options.width > 0) || (options?.height && options.height > 0))) {
        // 考虑设备像素比，但限制最大宽度避免过度放大
        const devicePixelRatio = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1
        const targetWidth = options.width ? Math.min(options.width * devicePixelRatio, options.width * 2) : undefined
        const targetHeight = options.height ? Math.min(options.height * devicePixelRatio, options.height * 2) : undefined

        const widthParam = targetWidth ? `,w_${Math.ceil(targetWidth)}` : ''
        const heightParam = targetHeight ? `,h_${Math.ceil(targetHeight)}` : ''
        const ossParams = `x-oss-process=image/resize${widthParam}${heightParam}`
        // 可选：添加质量压缩参数提升性能
        const qualityParams = options.quality ? `,q_${options.quality}` : ''
        suffix += `&${ossParams}${qualityParams}`
    }

    return `${imageUrl}${suffix}` as T
}

/**
 * iOS Safari canvas尺寸限制
 * 参考: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/canvas#maximum_canvas_size
 */
const MAX_CANVAS_SIZE = 16777216 // 16MB，iOS Safari的安全限制
const MAX_CANVAS_DIMENSION = 4096 // 单边最大尺寸

/**
 * 计算适合的canvas尺寸，避免超出iOS限制
 * @param width 原始宽度
 * @param height 原始高度
 * @returns 调整后的尺寸
 */
const calculateSafeCanvasSize = (width: number, height: number): { width: number; height: number } => {
    // 如果尺寸在安全范围内，直接返回
    if (width * height <= MAX_CANVAS_SIZE && width <= MAX_CANVAS_DIMENSION && height <= MAX_CANVAS_DIMENSION) {
        return { width, height }
    }

    // 计算缩放比例
    const aspectRatio = width / height
    let newWidth = width
    let newHeight = height

    // 首先确保单边尺寸不超限
    if (width > MAX_CANVAS_DIMENSION) {
        newWidth = MAX_CANVAS_DIMENSION
        newHeight = newWidth / aspectRatio
    }
    if (newHeight > MAX_CANVAS_DIMENSION) {
        newHeight = MAX_CANVAS_DIMENSION
        newWidth = newHeight * aspectRatio
    }

    // 然后确保总像素数不超限
    if (newWidth * newHeight > MAX_CANVAS_SIZE) {
        const scale = Math.sqrt(MAX_CANVAS_SIZE / (newWidth * newHeight))
        newWidth = Math.floor(newWidth * scale)
        newHeight = Math.floor(newHeight * scale)
    }

    return { width: Math.max(1, newWidth), height: Math.max(1, newHeight) }
}

/**
 * 将img标签转换为canvas，解决iOS截图中图片无法显示的问题
 * @param imgElement img元素
 * @returns Promise<HTMLCanvasElement>
 */
export const convertImgToCanvas = (imgElement: HTMLImageElement): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
            reject(new Error('无法获取canvas上下文'))
            return
        }

        // 获取img元素的显示尺寸
        const rect = imgElement.getBoundingClientRect()
        const displayWidth = rect.width
        const displayHeight = rect.height

        if (displayWidth === 0 || displayHeight === 0) {
            reject(new Error('图片元素尺寸为0'))
            return
        }

        // 计算安全的canvas尺寸
        const safeSize = calculateSafeCanvasSize(displayWidth, displayHeight)
        canvas.width = safeSize.width
        canvas.height = safeSize.height

        // 复制img元素的样式到canvas
        const computedStyle = window.getComputedStyle(imgElement)
        canvas.style.cssText = imgElement.style.cssText
        canvas.style.width = `${displayWidth}px`
        canvas.style.height = `${displayHeight}px`
        canvas.style.objectFit = computedStyle.objectFit
        canvas.style.borderRadius = computedStyle.borderRadius
        canvas.style.border = computedStyle.border
        canvas.className = imgElement.className
        canvas.id = imgElement.id

        // 创建新的Image对象来加载图片
        const image = new Image()
        image.crossOrigin = 'anonymous' // 处理跨域问题

        image.addEventListener('load', () => {
            try {
                // 清除canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height)

                // 根据object-fit样式绘制图片
                const objectFit = computedStyle.objectFit || 'fill'
                drawImageWithObjectFit(ctx, image, 0, 0, canvas.width, canvas.height, objectFit)

                resolve(canvas)
            } catch (error) {
                reject(error)
            }
        })

        image.addEventListener('error', () => {
            reject(new Error(`图片加载失败: ${imgElement.src}`))
        })

        // 如果img元素已经有src，直接使用；否则尝试从data-src获取
        const src = imgElement.src || imgElement.getAttribute('data-src') || ''
        if (src) {
            // 添加时间戳避免缓存问题
            const separator = src.includes('?') ? '&' : '?'
            image.src = `${src}${separator}_t=${Date.now()}`
        } else {
            reject(new Error('未找到图片源'))
        }
    })
}

/**
 * 根据object-fit样式绘制图片到canvas
 * @param ctx canvas上下文
 * @param image 图片对象
 * @param dx canvas目标x坐标
 * @param dy canvas目标y坐标
 * @param dw canvas目标宽度
 * @param dh canvas目标高度
 * @param objectFit object-fit样式值
 */
const drawImageWithObjectFit = (
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
    objectFit: string
) => {
    const imageRatio = image.naturalWidth / image.naturalHeight
    const canvasRatio = dw / dh

    let drawWidth = dw
    let drawHeight = dh
    let offsetX = dx
    let offsetY = dy

    switch (objectFit) {
        case 'contain': {
            if (imageRatio > canvasRatio) {
                drawHeight = dw / imageRatio
                offsetY = dy + (dh - drawHeight) / 2
            } else {
                drawWidth = dh * imageRatio
                offsetX = dx + (dw - drawWidth) / 2
            }
            break
        }
        case 'cover': {
            if (imageRatio > canvasRatio) {
                drawWidth = dh * imageRatio
                offsetX = dx + (dw - drawWidth) / 2
            } else {
                drawHeight = dw / imageRatio
                offsetY = dy + (dh - drawHeight) / 2
            }
            break
        }
        default: {
            // 默认填充整个区域 (包括 'fill' 情况)，使用原始参数值
            // drawWidth, drawHeight, offsetX, offsetY 保持初始值
            break
        }
    }

    ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight)
}

/**
 * 在指定容器内将所有img标签转换为canvas
 * @param container 容器元素
 * @returns Promise<{success: number, failed: number, total: number}>
 */
export const convertAllImgsToCanvas = async (container: HTMLElement): Promise<{ success: number; failed: number; total: number }> => {
    const images = container.querySelectorAll('img')

    if (images.length === 0) {
        return { success: 0, failed: 0, total: 0 }
    }

    let successCount = 0
    let failedCount = 0

    const conversions = [...images].map(async img => {
        try {
            const canvas = await convertImgToCanvas(img)
            // 在img元素的父元素中用canvas替换img
            if (img.parentNode) {
                img.parentNode.insertBefore(canvas, img)
                img.remove()
                successCount++
                return true
            }
            failedCount++
            return false
        } catch {
            failedCount++
            // 转换失败时保持原img元素
            return false
        }
    })

    await Promise.allSettled(conversions)

    return { success: successCount, failed: failedCount, total: images.length }
}

/**
 * 检查元素是否过大，需要特殊处理
 * @param element 要检查的元素
 * @returns 是否过大
 */
export const isElementTooLarge = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect()
    const totalPixels = rect.width * rect.height

    // 如果总像素数超过8MB或单边超过3000px，认为过大
    return totalPixels > 8000000 || rect.width > 3000 || rect.height > 3000
}

/**
 * 为大元素添加特殊样式以提高截图成功率
 * @param element 目标元素
 * @returns 恢复函数
 */
export const applyLargeElementOptimization = (element: HTMLElement): (() => void) => {
    const originalStyles = {
        transform: element.style.transform,
        willChange: element.style.willChange,
        backfaceVisibility: element.style.backfaceVisibility,
        perspective: element.style.perspective
    }

    // 应用优化样式
    element.style.transform = element.style.transform || 'translateZ(0)'
    element.style.willChange = 'transform'
    element.style.backfaceVisibility = 'hidden'
    element.style.perspective = '1000px'

    // 返回恢复函数
    return () => {
        Object.entries(originalStyles).forEach(([key, value]) => {
            if (value) {
                ;(element.style as unknown as Record<string, string>)[key] = value
            } else {
                element.style.removeProperty(key.replace(/([A-Z])/g, '-$1').toLowerCase())
            }
        })
    }
}
